package com.essntl.features.contact.ui

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.contact.domain.repository.ContactRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class ContactViewModel(
    private val state: MutableStateFlow<ContactState>,
    private val contactRepository: ContactRepository,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    fun onEvent(event: ContactEvent) {
        when (event) {
            is ContactEvent.GetContacts -> getAllContacts(event)
            is ContactEvent.GetOrganizationContacts -> getOrganizationContacts(event)
            is ContactEvent.GetContact -> getContact(event)
            is ContactEvent.AddContact -> addContact()
            is ContactEvent.EditContact -> editContact()
            is ContactEvent.UpdateContactFields -> updateContactFields(event)
            is ContactEvent.AddRemoveAddress -> addRemoveAddress(event)
            is ContactEvent.ClearContactState -> clearContactState()
            is ContactEvent.ClearSuccessState -> clearSuccessState()
        }
    }

    fun isContactChanged(): Boolean {
        val oldContact = state.value.getContact.data ?: ContactModel.empty
        val newContact = oldContact.getChangedFields(state.value.contact.value)
        return !newContact.isEmpty
    }

    private fun getAllContacts(event: ContactEvent.GetContacts) =
        getData(
            getData = {
                contactRepository.getContactList(
                    type = event.type,
                    searchText = event.searchText,
                )
            },
            getState = { state.value.getAllContacts },
            setState = { newState -> state.update { it.copy(getAllContacts = newState) } },
        )

    private fun getOrganizationContacts(event: ContactEvent.GetOrganizationContacts) =
        getData(
            getData = {
                contactRepository.getOrganizationContacts(
                    organizationId = event.organizationId,
                    searchText = event.searchText,
                )
            },
            getState = { state.value.getAllContacts },
            setState = { newState -> state.update { it.copy(getAllContacts = newState) } },
        )

    private fun getContact(event: ContactEvent.GetContact) =
        getData(
            getData = { contactRepository.getContact(id = event.id) },
            getState = { state.value.getContact },
            setState = { newState -> state.update { it.copy(getContact = newState) } },
            onSuccess = { contact -> state.value.contact.value = contact },
        )

    private fun editContact() {
        if (!isContactChanged()) {
            state.update {
                it.copy(
                    editContact = State.success(state.value.contact.value),
                )
            }

            return
        }

        getData(
            getData = { contactRepository.update(state.value.contact.value) },
            getState = { state.value.editContact },
            setState = { newState -> state.update { it.copy(editContact = newState) } },
            onSuccess = { contact ->
                state.update {
                    it.copy(
                        getContact = State.success(contact),
                    )
                }

                state.value.contact.value = contact
            },
        )
    }

    private fun addContact() =
        getData(
            getData = { contactRepository.insert(state.value.contact.value) },
            getState = { state.value.editContact },
            setState = { newState -> state.update { it.copy(editContact = newState) } },
            onSuccess = { contact ->
                state.update {
                    it.copy(
                        getContact = State.success(contact),
                    )
                }

                state.value.contact.value = contact
            },
        )

    private fun updateContactFields(event: ContactEvent.UpdateContactFields) {
        state.value.contact.value = event.contact
    }

    private fun addRemoveAddress(event: ContactEvent.AddRemoveAddress) =
        state.update {
            it.copy(
                addAddress = event.addAddress ?: !it.addAddress,
            )
        }

    private fun clearContactState() =
        state.update {
            it.copy(
                getAllContacts = State(),
                getContact = State(),
                editContact = State(),
                addAddress = false,
            )
        }.also {
            state.value.contact.value = ContactModel.empty
        }

    private fun clearSuccessState() =
        state.update {
            it.copy(
                editContact = State(),
            )
        }
}